import { useEffect, useMemo, useState, useContext, useRef } from "react";
import { getApiUrl } from "../utils/api";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import LoginModal from "../components/LoginModal";
import Skeleton, { SkeletonText } from "../components/Skeleton";
import { FaRegBookmark, FaBookmark } from "react-icons/fa";
import { Link } from "react-router-dom";
import {
  FaRegComment,
  FaEye,
  FaRegPaperPlane,
  FaSync,
  FaFlag,
  FaThumbtack,
  FaUsers,
} from "react-icons/fa";
import ShareToGroupModal from "../components/ShareToGroupModal";
import { UserContext } from "../context/UserContext";
import { useChat } from "../context/ChatContext";
import { useToast } from "../context/ToastContext";
import Modal from "../components/Modal";
import LikeHeart, { LIKE_BURST_MS } from "../components/LikeHeart";
import "./PoemDetail.css";
import { contentToHtml } from "../lib/contentToHtml";
import BadgeIcon from "../components/BadgeIcon";
import EmptyState from "../components/EmptyState";
import HomeRightSidebar from "../components/HomeRightSidebar";
import {
  getAvatarUrl,
  getPoemImageUrl,
  DEFAULT_AVATAR_DATA_URL,
  FALLBACK_AVATAR_SVG,
} from "../utils/avatar";
import { getCommentCount } from "../utils/poemStats";

const ACCENT = "#f26522";

// ...

function PoemDetail({ navbarRef }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [poem, setPoem] = useState(null);
  const { user, setUser } = useContext(UserContext);
  const { getUpdatedPoem, clearPoemUpdate } = useChat();
  const toast = useToast();
  const [commentText, setCommentText] = useState("");
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [editCommentId, setEditCommentId] = useState(null);
  const [editText, setEditText] = useState("");
  const [replyText, setReplyText] = useState("");
  const [replyingToCommentId, setReplyingToCommentId] = useState(null);
  const [replyingToReplyId, setReplyingToReplyId] = useState(null);
  const [replyingToUsername, setReplyingToUsername] = useState(null);
  const [editReply, setEditReply] = useState({
    commentId: null,
    replyId: null,
  });
  const [editReplyText, setEditReplyText] = useState("");
  const [copied, setCopied] = useState(false);
  const [showShareToGroup, setShowShareToGroup] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [modal, setModal] = useState({
    open: false,
    title: "",
    message: "",
    type: "info",
    onConfirm: null,
    confirmText: "",
  });
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [likeAnimating, setLikeAnimating] = useState(false);
  const [commentLikeAnimating, setCommentLikeAnimating] = useState({});
  const [replyLikeAnimating, setReplyLikeAnimating] = useState({});
  const [saving, setSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [collabLists, setCollabLists] = useState({
    collaborators: [],
    invites: [],
  });
  const commentInputRef = useRef(null);
  const replyInputRef = useRef(null);
  const [expandedReplyThreads, setExpandedReplyThreads] = useState({});
  const [commentsPagination, setCommentsPagination] = useState({
    currentPage: 1,
    hasNextPage: false,
    totalItems: 0,
  });
  const [loadingMoreComments, setLoadingMoreComments] = useState(false);
  const REPLY_PREVIEW_COUNT = 3;

  const syncCommentsMeta = (poemData) => {
    if (poemData?.commentsPagination) {
      setCommentsPagination(poemData.commentsPagination);
      return;
    }
    const total =
      poemData?.commentCount ?? poemData?.comments?.length ?? 0;
    setCommentsPagination({
      currentPage: 1,
      hasNextPage: false,
      totalItems: total,
      totalPages: 1,
    });
  };

  const applyPoemUpdate = (updated) => {
    const next = updated?.poem || updated;
    if (!next) return;

    setPoem((prev) => {
      // Keep already-loaded comment pages when the server returns only page 1
      // (initial GET shape) or a full dump during mutations.
      if (
        prev?.commentsPagination?.hasNextPage &&
        !next.commentsPagination &&
        Array.isArray(next.comments) &&
        Array.isArray(prev.comments)
      ) {
        const byId = new Map(
          next.comments.map((c) => [String(c._id), c])
        );
        let mergedComments = prev.comments.map(
          (c) => byId.get(String(c._id)) || c
        );
        const prevCount =
          prev.commentCount ??
          prev.commentsPagination?.totalItems ??
          prev.comments.length;
        const nextCount = next.comments.length;

        // Newly added comment (usually at the end)
        if (nextCount === prevCount + 1) {
          const newest = next.comments[next.comments.length - 1];
          if (
            newest &&
            !mergedComments.some((c) => String(c._id) === String(newest._id))
          ) {
            mergedComments = [...mergedComments, newest];
          }
        }

        // Deleted comment
        if (nextCount < prevCount) {
          const alive = new Set(next.comments.map((c) => String(c._id)));
          mergedComments = mergedComments.filter((c) =>
            alive.has(String(c._id))
          );
        }

        const commentsPagination = {
          ...prev.commentsPagination,
          totalItems: nextCount,
          hasNextPage: mergedComments.length < nextCount,
        };
        setCommentsPagination(commentsPagination);
        return {
          ...next,
          comments: mergedComments,
          commentCount: nextCount,
          commentsPagination,
        };
      }

      if (
        prev?.comments?.length &&
        next.commentsPagination &&
        Array.isArray(next.comments) &&
        prev.comments.length > next.comments.length
      ) {
        const page1ById = new Map(
          next.comments.map((c) => [String(c._id), c])
        );
        const mergedComments = prev.comments.map(
          (c) => page1ById.get(String(c._id)) || c
        );
        const total =
          next.commentCount ??
          prev.commentCount ??
          prev.commentsPagination?.totalItems ??
          mergedComments.length;
        const commentsPagination = {
          ...prev.commentsPagination,
          totalItems: total,
          hasNextPage: mergedComments.length < total,
        };
        setCommentsPagination(commentsPagination);
        return {
          ...next,
          comments: mergedComments,
          commentCount: total,
          commentsPagination,
        };
      }

      syncCommentsMeta(next);
      return next;
    });
  };

  const loadMoreComments = async () => {
    if (!id || loadingMoreComments || !commentsPagination.hasNextPage) return;
    setLoadingMoreComments(true);
    try {
      const nextPage = (commentsPagination.currentPage || 1) + 1;
      const res = await fetch(
        getApiUrl(`/api/poems/${id}/comments?page=${nextPage}&limit=10`)
      );
      if (!res.ok) throw new Error("Failed to load comments");
      const data = await res.json();
      setPoem((prev) => ({
        ...prev,
        comments: [...(prev?.comments || []), ...(data.comments || [])],
        commentCount: data.commentCount ?? prev?.commentCount,
        commentsPagination: data.pagination,
      }));
      if (data.pagination) setCommentsPagination(data.pagination);
    } catch (err) {
      console.error("Error loading comments:", err);
    } finally {
      setLoadingMoreComments(false);
    }
  };

  // Mention autocomplete state
  const [mention, setMention] = useState({
    open: false,
    target: null, // 'comment' | 'reply'
    start: 0,
    end: 0,
    query: "",
    items: [],
    index: 0,
  });
  const mentionDebounceRef = useRef(null);

  useEffect(() => {
    // Scroll to top when poem page loads (prevents jumping to comments section)
    // Clear any hash fragments that might cause unwanted scrolling
    if (window.location.hash) {
      window.history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search
      );
    }
    window.scrollTo(0, 0);

    const fetchPoem = async () => {
      try {
        const res = await fetch(getApiUrl(`/api/poems/${id}`), {
          headers: {
            "Cache-Control": "no-cache",
          },
        });

        if (res.status === 404) {
          // Poem not found, redirect to 404 page
          navigate("/404", { replace: true });
          return;
        }

        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }

        const data = await res.json();
        // Server may return the poem object directly or wrapped
        const loadedPoem = data?.poem || data;
        applyPoemUpdate(loadedPoem);

        // Ensure we're still at top after poem loads (in case of layout shift)
        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 100);
      } catch (error) {
        console.error("Error fetching poem:", error);
        // Redirect to 404 page on any error
        navigate("/404", { replace: true });
      }
    };

    // Fetch latest user profile for avatar
    const fetchProfile = async () => {
      try {
        const res = await fetch(getApiUrl("/api/user/profile"), {
          credentials: "include", // Include cookies for authentication
        });
        if (!res.ok) return;
        const data = await res.json();
        setUser(data.user);
        localStorage.setItem("user", JSON.stringify(data.user));
      } catch (err) {}
    };

    fetchPoem();
    fetchProfile();
    // load collaborators
    (async () => {
      try {
        const r = await fetch(
          getApiUrl(`/api/poems/${id}/collaborators`)
        );
        if (r.ok) setCollabLists(await r.json());
      } catch {}
    })();
  }, [id, navigate, setUser]);

  useEffect(() => {
    setExpandedReplyThreads({});
    setCommentsPagination({
      currentPage: 1,
      hasNextPage: false,
      totalItems: 0,
    });
  }, [id]);

  useEffect(() => {
    if (!id) return undefined;
    const viewKey = `poem-view:${id}`;
    if (sessionStorage.getItem(viewKey)) return undefined;

    sessionStorage.setItem(viewKey, "1");

    fetch(getApiUrl(`/api/poems/${id}/view`), {
      method: "POST",
      credentials: "include",
    })
      .then((viewRes) => (viewRes.ok ? viewRes.json() : null))
      .then((viewData) => {
        if (!viewData) {
          sessionStorage.removeItem(viewKey);
          return;
        }
        if (typeof viewData.viewCount === "number" && !viewData.skipped) {
          setPoem((prev) =>
            prev ? { ...prev, viewCount: viewData.viewCount } : prev
          );
        }
      })
      .catch(() => {
        sessionStorage.removeItem(viewKey);
      });

    return undefined;
  }, [id]);

  // Handle real-time poem updates
  useEffect(() => {
    const updatedPoem = getUpdatedPoem(id);
    if (updatedPoem) {
      applyPoemUpdate(updatedPoem);
      clearPoemUpdate(id);
    }
  }, [getUpdatedPoem, clearPoemUpdate, id]);

  // Auto-focus reply input when reply button is clicked
  useEffect(() => {
    if (replyingToCommentId && replyInputRef.current) {
      // Small delay to ensure DOM has updated
      setTimeout(() => {
        replyInputRef.current?.focus();
      }, 100);
    }
  }, [replyingToCommentId, replyingToReplyId]);

  const startReply = (
    commentId,
    { replyId = null, username = null, threadKey = null, allReplies = [] } = {}
  ) => {
    if (!user) {
      navigate("/login");
      return;
    }

    if (
      replyId &&
      threadKey &&
      allReplies.length > REPLY_PREVIEW_COUNT &&
      !expandedReplyThreads[threadKey]
    ) {
      const replyIndex = allReplies.findIndex(
        (r) => String(r._id) === String(replyId)
      );
      if (replyIndex >= REPLY_PREVIEW_COUNT) {
        setExpandedReplyThreads((prev) => ({ ...prev, [threadKey]: true }));
      }
    }

    setReplyingToCommentId(commentId);
    setReplyingToReplyId(replyId);
    setReplyingToUsername(username);
    setReplyText(username ? `@${username} ` : "");
  };

  const renderReplyForm = (commentId) => (
    <div className="poem-detail-reply-form">
      {replyingToReplyId && replyingToUsername && (
        <p className="text-xs poem-detail-comment-time mb-1">
          Replying to{" "}
          <span className="font-semibold poem-detail-comment-author">
            @{replyingToUsername}
          </span>
        </p>
      )}
      <form
        onSubmit={(e) => handleReplySubmit(e, commentId)}
        className="mt-2 flex flex-col sm:flex-row gap-2"
      >
        <div className="flex-1 relative min-w-0">
          <input
            type="text"
            placeholder="Write a reply... (use @ to mention users)"
            ref={replyInputRef}
            value={replyText}
            onChange={(e) => {
              setReplyText(e.target.value);
              handleMentionChange(e, "reply");
            }}
            onKeyDown={(e) => handleMentionKeyDown(e, "reply")}
            className="poem-detail-input w-full p-2 rounded text-sm"
            required
          />
          {mention.open && mention.target === "reply" && (
            <div className="mention-suggest absolute left-0 mt-1 z-50">
              <ul>
                {(mention.items || []).map((u, i) => (
                  <li
                    key={u._id || i}
                    className={i === mention.index ? "active" : ""}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      applyMention(u.username);
                    }}
                  >
                    <img
                      src={getAvatarUrl(u.avatar)}
                      alt="avatar"
                      className="w-6 h-6 rounded-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src =
                          e.currentTarget.src === DEFAULT_AVATAR_DATA_URL
                            ? FALLBACK_AVATAR_SVG
                            : DEFAULT_AVATAR_DATA_URL;
                      }}
                    />
                    <span className="font-medium">{u.username}</span>
                    {u.name && (
                      <span className="text-[#777777] ml-1">({u.name})</span>
                    )}
                  </li>
                ))}
                {(!mention.items || mention.items.length === 0) && (
                  <li className="text-sm text-[#777777] px-3 py-2">No matches</li>
                )}
              </ul>
            </div>
          )}
        </div>
        <button
          type="submit"
          className="poem-detail-btn px-4 py-1.5 text-sm transition-colors shrink-0"
        >
          Reply
        </button>
      </form>
    </div>
  );

  // Periodic refresh as fallback
  useEffect(() => {
    if (!id) return;

    const interval = setInterval(async () => {
      try {
        const res = await fetch(getApiUrl(`/api/poems/${id}`));
        const data = await res.json();
        applyPoemUpdate(data?.poem || data);
      } catch (error) {
        console.error("Error refreshing poem:", error);
      }
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [id]);

  // Load initial saved status for this poem
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!user || !id) return;
        const res = await fetch(getApiUrl(`/api/poems/${id}/saved`), {
          credentials: "include",
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setIsSaved(Boolean(data.saved));
      } catch {}
    })();
    return () => {
      cancelled = true;
    };
  }, [user, id]);

  useEffect(() => {
    if (likeAnimating) {
      const timeout = setTimeout(() => setLikeAnimating(false), LIKE_BURST_MS);
      return () => clearTimeout(timeout);
    }
  }, [likeAnimating]);

  const handleLike = async () => {
    if (!user) {
      setModal({
        open: true,
        title: "Login Required",
        message: "Please log in to like poems!",
        type: "error",
      });
      return;
    }

    const alreadyLiked =
      Array.isArray(poem?.likes) &&
      poem.likes.some((like) => String(like._id || like) === String(user._id));

    try {
      if (!alreadyLiked) setLikeAnimating(true);
      const res = await fetch(getApiUrl(`/api/poems/${id}/like`), {
        method: "PUT",
        credentials: "include", // Include cookies for authentication
      });

      if (!res.ok) {
        setLikeAnimating(false);
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const updated = await res.json();
      applyPoemUpdate(updated);
    } catch (error) {
      console.error("Error liking poem:", error);
      setLikeAnimating(false);
      setModal({
        open: true,
        title: "Error",
        message: "Failed to like poem. Please try again.",
        type: "error",
      });
    }
  };

  const handleToggleSave = async () => {
    if (!user) {
      setModal({
        open: true,
        title: "Login Required",
        message: "Please log in to save poems!",
        type: "error",
      });
      return;
    }
    if (!id) return;
    if (saving) return;
    try {
      setSaving(true);
      const res = await fetch(getApiUrl(`/api/poems/${id}/save`), {
        method: "PUT",
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();
      const nextSaved = Boolean(data.saved);
      setIsSaved(nextSaved);
      // Show toast notification
      toast.success(
        nextSaved ? "Poem saved" : "Poem unsaved",
        { title: nextSaved ? "Saved" : "Unsaved" }
      );
    } catch (error) {
      console.error("Error saving poem:", error);
      toast.error("Failed to save poem");
    } finally {
      setSaving(false);
    }
  };

  const handleLikeComment = async (commentId) => {
    if (!user) {
      setModal({
        open: true,
        title: "Login Required",
        message: "Please log in to like comments!",
        type: "error",
      });
      return;
    }

    try {
      const comment = poem?.comments?.find(
        (c) => String(c._id) === String(commentId)
      );
      const alreadyLiked =
        Array.isArray(comment?.likes) &&
        comment.likes.some((like) => String(like._id || like) === String(user._id));
      if (!alreadyLiked) {
        setCommentLikeAnimating((prev) => ({ ...prev, [commentId]: true }));
      }
      const res = await fetch(
        getApiUrl(`/api/poems/${id}/comments/${commentId}/like`),
        {
          method: "PUT",
          credentials: "include", // Include cookies for authentication
        }
      );

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.msg || `HTTP error! status: ${res.status}`);
      }

      const updated = await res.json();
      applyPoemUpdate(updated);
    } catch (error) {
      console.error("Error liking comment:", error);
      // Only show error modal for actual failures, not for deleted users (which now work silently)
      const errorMessage = error.message || "Failed to like comment. Please try again.";
      if (!errorMessage.includes("deleted") && !errorMessage.includes("not found")) {
        setModal({
          open: true,
          title: "Error",
          message: errorMessage,
          type: "error",
        });
      }
    } finally {
      setTimeout(() => {
        setCommentLikeAnimating((prev) => ({ ...prev, [commentId]: false }));
      }, LIKE_BURST_MS);
    }
  };

  const handleLikeReply = async (commentId, replyId) => {
    if (!user) {
      setModal({
        open: true,
        title: "Login Required",
        message: "Please log in to like replies!",
        type: "error",
      });
      return;
    }

    try {
      const replyKey = `${commentId}-${replyId}`;
      const parent = poem?.comments?.find(
        (c) => String(c._id) === String(commentId)
      );
      const reply = parent?.replies?.find(
        (r) => String(r._id) === String(replyId)
      );
      const alreadyLiked =
        Array.isArray(reply?.likes) &&
        reply.likes.some((like) => String(like._id || like) === String(user._id));
      if (!alreadyLiked) {
        setReplyLikeAnimating((prev) => ({ ...prev, [replyKey]: true }));
      }
      const res = await fetch(
        getApiUrl(`/api/poems/${id}/comments/${commentId}/replies/${replyId}/like`),
        {
          method: "PUT",
          credentials: "include", // Include cookies for authentication
        }
      );

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.msg || `HTTP error! status: ${res.status}`);
      }

      const updated = await res.json();
      applyPoemUpdate(updated);
    } catch (error) {
      console.error("Error liking reply:", error);
      // Only show error modal for actual failures, not for deleted users (which now work silently)
      const errorMessage = error.message || "Failed to like reply. Please try again.";
      if (!errorMessage.includes("deleted") && !errorMessage.includes("not found")) {
        setModal({
          open: true,
          title: "Error",
          message: errorMessage,
          type: "error",
        });
      }
    } finally {
      setTimeout(() => {
        const replyKey = `${commentId}-${replyId}`;
        setReplyLikeAnimating((prev) => ({ ...prev, [replyKey]: false }));
      }, LIKE_BURST_MS);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();

    // Check if user is authenticated
    if (!user) {
      navigate("/login");
      return;
    }

    try {
      const res = await fetch(getApiUrl(`/api/poems/${id}/comment`), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Include cookies for authentication
        body: JSON.stringify({ text: commentText }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || `HTTP error! status: ${res.status}`);
      }

      const updated = await res.json();
      applyPoemUpdate(updated);
      setCommentText("");
    } catch (error) {
      console.error("Error adding comment:", error);
      setModal({
        open: true,
        title: "Error",
        message: "Failed to add comment. Please try again.",
        type: "error",
      });
    }
  };

  const handleEditSubmit = async (e, commentId) => {
    e.preventDefault();

    // Check if user is authenticated
    if (!user) {
      navigate("/login");
      return;
    }

    try {
      const res = await fetch(
        getApiUrl(`/api/poems/${poem._id}/comments/${commentId}`),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include", // Include cookies for authentication
          body: JSON.stringify({ text: editText }),
        }
      );

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || `HTTP error! status: ${res.status}`);
      }

      const updated = await res.json();
      applyPoemUpdate(updated);
      setEditCommentId(null);
      setEditText("");
    } catch (err) {
      console.error("Edit comment failed:", err);
      setModal({
        open: true,
        title: "Error",
        message: "Failed to edit comment. Please try again.",
        type: "error",
      });
    }
  };

  const handleDeleteComment = async (commentId) => {
    // Check if user is authenticated
    if (!user) {
      navigate("/login");
      return;
    }

    try {
      const res = await fetch(
        getApiUrl(`/api/poems/${poem._id}/comments/${commentId}`),
        {
          method: "DELETE",
          credentials: "include", // Include cookies for authentication
        }
      );

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || `HTTP error! status: ${res.status}`);
      }

      const updated = await res.json();
      applyPoemUpdate(updated);
      setModal({
        open: false,
        title: "",
        message: "",
        type: "info",
        onConfirm: null,
        confirmText: "",
      });
    } catch (err) {
      console.error("Failed to delete comment:", err);
      setModal({
        open: true,
        title: "Error",
        message: "Failed to delete comment. Please try again.",
        type: "error",
        onConfirm: null,
        confirmText: "",
      });
    }
  };

  const handleReplySubmit = async (e, commentId) => {
    e.preventDefault();

    // Check if user is authenticated
    if (!user) {
      navigate("/login");
      return;
    }

    try {
      const res = await fetch(
        getApiUrl(`/api/poems/${poem._id}/comments/${commentId}/reply`),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include", // Include cookies for authentication
          body: JSON.stringify({ text: replyText }),
        }
      );

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || `HTTP error! status: ${res.status}`);
      }

      const updated = await res.json();
      applyPoemUpdate(updated);
      setReplyText("");
      setReplyingToCommentId(null);
      setReplyingToReplyId(null);
      setReplyingToUsername(null);
    } catch (err) {
      console.error("Reply failed:", err);
      setModal({
        open: true,
        title: "Error",
        message: "Failed to add reply. Please try again.",
        type: "error",
      });
    }
  };

  // Handler to edit a reply
  const handleEditReplySubmit = async (e, commentId, replyId) => {
    e.preventDefault();

    // Check if user is authenticated
    if (!user) {
      navigate("/login");
      return;
    }

    try {
      const res = await fetch(
        getApiUrl(`/api/poems/${poem._id}/comments/${commentId}/replies/${replyId}`),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include", // Include cookies for authentication
          body: JSON.stringify({ text: editReplyText }),
        }
      );

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || `HTTP error! status: ${res.status}`);
      }

      const updated = await res.json();
      applyPoemUpdate(updated);
      setEditReply({ commentId: null, replyId: null });
      setEditReplyText("");
    } catch (err) {
      console.error("Edit reply failed:", err);
      setModal({
        open: true,
        title: "Error",
        message: "Failed to edit reply. Please try again.",
        type: "error",
      });
    }
  };

  // Handler to delete a reply
  const handleDeleteReply = async (commentId, replyId) => {
    // Check if user is authenticated
    if (!user) {
      navigate("/login");
      return;
    }

    try {
      const res = await fetch(
        getApiUrl(`/api/poems/${poem._id}/comments/${commentId}/replies/${replyId}`),
        {
          method: "DELETE",
          credentials: "include", // Include cookies for authentication
        }
      );

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || `HTTP error! status: ${res.status}`);
      }

      const updated = await res.json();
      applyPoemUpdate(updated);
      setModal({
        open: false,
        title: "",
        message: "",
        type: "info",
        onConfirm: null,
        confirmText: "",
      });
    } catch (err) {
      console.error("Failed to delete reply:", err);
      setModal({
        open: true,
        title: "Error",
        message: "Failed to delete reply. Please try again.",
        type: "error",
        onConfirm: null,
        confirmText: "",
      });
    }
  };

  const handleDeletePoem = async () => {
    try {
      const res = await fetch(getApiUrl(`/api/poems/${poem._id}`), {
        method: "DELETE",
        credentials: "include", // Include cookies for authentication
      });

      if (res.ok) {
        toast.success("Poem deleted successfully");
        if (
          navbarRef &&
          navbarRef.current &&
          navbarRef.current.fetchNotifications
        ) {
          navbarRef.current.fetchNotifications();
        }
        setTimeout(() => navigate("/"), 1200);
      } else {
        const data = await res.json();
        throw new Error(data?.error || "Failed to delete poem");
      }
    } catch (err) {
      console.error("Error deleting poem:", err);
      setModal({
        open: true,
        title: "Error",
        message: "Something went wrong.",
        type: "error",
      });
    }
  };

  const handleShare = () => {
    const url = window.location.origin + `/poems/${id}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      toast.success("Link copied to clipboard!");
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const canShareToGroup = useMemo(() => {
    return !!(user && poem && !poem.isDraft);
  }, [user, poem]);

  const isPinnedByMe = !!(
    user &&
    user.pinnedPoem &&
    String(user.pinnedPoem) === String(poem?._id)
  );

  const handlePinPoem = async () => {
    if (!user) return;
    try {
      // Prefer URL param endpoint to avoid any body parsing issues
      const res = await fetch(
        getApiUrl(`/api/user/pin-poem/${poem._id}`),
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
          },
          credentials: "include",
        }
      );
      if (res.ok) {
        const data = await res.json();
        const nextUser = { ...(user || {}), pinnedPoem: data.pinnedPoemId };
        setUser(nextUser);
        localStorage.setItem("user", JSON.stringify(nextUser));
        toast.success("Poem pinned to your profile");
      }
    } catch (e) {
      // ignore
    }
  };

  const handleUnpinPoem = async () => {
    if (!user) return;
    try {
      const res = await fetch(getApiUrl("/api/user/pin-poem"), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
        },
        credentials: "include",
      });
      if (res.ok) {
        const nextUser = { ...(user || {}), pinnedPoem: null };
        setUser(nextUser);
        localStorage.setItem("user", JSON.stringify(nextUser));
        toast.success("Poem unpinned from your profile");
      }
    } catch (e) {
      // ignore
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const res = await fetch(getApiUrl(`/api/poems/${id}`));
      const data = await res.json();
      applyPoemUpdate(data);
    } catch (error) {
      console.error("Error refreshing poem:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleReport = async () => {
    if (!reportReason.trim()) {
      setModal({
        open: true,
        title: "Error",
        message: "Please provide a reason for reporting this content.",
        type: "error",
      });
      return;
    }

    try {
      const res = await fetch(
        getApiUrl(`/api/poems/${poem._id}/report`),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
          },
          credentials: "include",
          body: JSON.stringify({ reason: reportReason }),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        toast.success(
          "Content reported successfully. Thank you for helping keep our community safe."
        );
        setShowReportModal(false);
        setReportReason("");
      } else {
        setModal({
          open: true,
          title: "Error",
          message:
            data.error ||
            data.msg ||
            `Failed to report content. (${res.status})`,
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error reporting poem:", error);
      setModal({
        open: true,
        title: "Error",
        message: "Failed to report content. Please try again.",
        type: "error",
      });
    }
  };

  // Fetch user suggestions with debounce
  const fetchMentionSuggestions = (query) => {
    if (mentionDebounceRef.current) {
      clearTimeout(mentionDebounceRef.current);
    }
    mentionDebounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          getApiUrl(`/api/user/search?q=${encodeURIComponent(query)}`),
          {
            credentials: "include", // Include cookies for authentication
          }
        );
        const users = (await res.json()) || [];
        setMention((m) => ({ ...m, items: users.slice(0, 8), index: 0 }));
      } catch (e) {
        setMention((m) => ({ ...m, items: [], index: 0 }));
      }
    }, 120);
  };

  const handleMentionChange = (e, target) => {
    const input = e.target;
    const cursor = input.selectionStart || 0;
    const upToCursor = input.value.slice(0, cursor);
    const match = upToCursor.match(/(^|\s)@([a-zA-Z0-9_.-]{1,30})$/);
    if (match) {
      const query = match[2];
      const start = cursor - query.length - 1; // include '@'
      setMention((m) => ({
        ...m,
        open: true,
        target,
        start,
        end: cursor,
        query,
      }));
      if (query.length >= 1) {
        fetchMentionSuggestions(query);
      } else {
        setMention((m) => ({ ...m, items: [], index: 0 }));
      }
    } else {
      setMention((m) => ({ ...m, open: false, target: null, items: [] }));
    }
  };

  const applyMention = (username) => {
    const { target, start, end } = mention;
    if (!target) return;
    const insert = `@${username} `;
    if (target === "comment") {
      const before = commentText.slice(0, start);
      const after = commentText.slice(end);
      const next = before + insert + after;
      setCommentText(next);
      if (commentInputRef.current) {
        commentInputRef.current.focus();
        const caret = before.length + insert.length;
        commentInputRef.current.setSelectionRange(caret, caret);
      }
    } else if (target === "reply") {
      const before = replyText.slice(0, start);
      const after = replyText.slice(end);
      const next = before + insert + after;
      setReplyText(next);
      if (replyInputRef.current) {
        replyInputRef.current.focus();
        const caret = before.length + insert.length;
        replyInputRef.current.setSelectionRange(caret, caret);
      }
    }
    setMention({
      open: false,
      target: null,
      start: 0,
      end: 0,
      query: "",
      items: [],
      index: 0,
    });
  };

  const handleMentionKeyDown = (e, target) => {
    if (!mention.open || mention.target !== target) return;
    const hasItems = mention.items && mention.items.length > 0;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!hasItems) return;
      setMention((m) => ({ ...m, index: (m.index + 1) % m.items.length }));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!hasItems) return;
      setMention((m) => ({
        ...m,
        index: (m.index - 1 + m.items.length) % m.items.length,
      }));
    } else if (e.key === "Enter" || e.key === "Tab") {
      if (mention.items[mention.index]) {
        e.preventDefault();
        applyMention(mention.items[mention.index].username);
      } else {
        setMention((m) => ({ ...m, open: false }));
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setMention((m) => ({ ...m, open: false }));
    }
  };

  // Map known usernames to user IDs (author, commenters, repliers)
  const usernameToId = useMemo(() => {
    const map = new Map();
    if (poem?.author?.username && poem?.author?._id) {
      map.set(poem.author.username, poem.author._id);
    }
    if (Array.isArray(poem?.comments)) {
      poem.comments.forEach((c) => {
        if (c?.user?.username && c?.user?._id) {
          map.set(c.user.username, c.user._id);
        }
        if (Array.isArray(c?.replies)) {
          c.replies.forEach((r) => {
            if (r?.user?.username && r?.user?._id) {
              map.set(r.user.username, r.user._id);
            }
          });
        }
      });
    }
    return map;
  }, [poem]);

  // Render plain text with @mentions converted to links when the username is known
  const renderWithMentions = (text) => {
    if (!text) return null;
    const parts = String(text).split(/(@[a-zA-Z0-9_.-]{2,30})/g);
    return parts.map((part, idx) => {
      const mentionMatch = /^@([a-zA-Z0-9_.-]{2,30})$/.exec(part);
      if (mentionMatch) {
        const username = mentionMatch[1];
        const id = usernameToId.get(username);
        if (id) {
          return (
            <Link
              key={`m-${idx}`}
              to={`/users/${username}`}
              className="peer-home-link hover:underline"
            >
              @{username}
            </Link>
          );
        }
        return (
          <span key={`m-${idx}`} className="text-[#f26522] font-semibold">
            @{username}
          </span>
        );
      }
      return <span key={`t-${idx}`}>{part}</span>;
    });
  };

  if (!poem) {
    return (
      <div className="poem-detail-page poem-detail-page--panes">
        <div className="poem-detail-page__layout">
          <article className="poem-detail-page__card peer-home-card">
            <Skeleton className="h-8 w-2/3 mb-4 bg-[#eeeeee]" />
            <SkeletonText lines={5} className="mb-6" />
            <Skeleton className="h-6 w-1/3 mb-2 bg-[#eeeeee]" />
            <Skeleton className="h-6 w-24 bg-[#eeeeee]" />
          </article>
          <aside className="poem-detail-page__rail">
            <HomeRightSidebar />
          </aside>
        </div>
      </div>
    );
  }

  const poemHtml = contentToHtml(poem?.content);
  const plainText = (() => {
    const div = document.createElement("div");
    div.innerHTML = poemHtml || "";
    const text = div.textContent || div.innerText || "";
    return text.length > 160 ? text.slice(0, 160) + "..." : text;
  })();

  // Helper for time ago
  function timeAgo(date) {
    const now = new Date();
    const then = new Date(date);
    const seconds = Math.floor((now - then) / 1000);
    if (seconds < 60) return "just now";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days > 1 ? "s" : ""} ago`;
  }

  const getAvatar = (u) => getAvatarUrl(u?.avatar);

  return (
    <div className="poem-detail-page poem-detail-page--panes">
      <div className="poem-detail-page__layout">
        <article className="poem-detail-page__card peer-home-card">
      <Helmet>
        <title>{`${poem.title} – by ${
          poem.author?.username || "Unknown"
        } | PeerPoetry`}</title>
        <meta name="description" content={plainText || poem.title} />
        <link
          rel="canonical"
          href={`${window.location.origin}/poems/${poem._id}`}
        />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={`${poem.title} – PeerPoetry`} />
        <meta property="og:description" content={plainText || poem.title} />
        <meta
          property="og:url"
          content={`${window.location.origin}/poems/${poem._id}`}
        />
        <meta name="twitter:card" content="summary" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CreativeWork",
            name: poem.title,
            author: poem.author?.username
              ? { "@type": "Person", name: poem.author.username }
              : undefined,
            datePublished: poem.createdAt,
            dateModified: poem.updatedAt || poem.createdAt,
            inLanguage: "en",
            keywords: Array.isArray(poem.tags)
              ? poem.tags.join(",")
              : undefined,
            description: plainText,
            mainEntityOfPage: `${window.location.origin}/poems/${poem._id}`,
          })}
        </script>
      </Helmet>
      <Modal
        open={modal.open}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        onClose={() => setModal({ ...modal, open: false })}
        onConfirm={modal.onConfirm}
        confirmText={modal.confirmText}
      />
      <header className="poem-detail-page__toolbar">
        <div className="poem-detail-breadcrumb text-xs sm:text-sm break-words min-w-0">
          <span>Poetry</span> <span className="mx-1">/</span>{" "}
          <span className="poem-detail-title-crumb break-words">{poem.title}</span>
        </div>
        <div className="poem-detail-page__actions flex flex-wrap justify-end gap-2 sm:gap-3">
        <button
          onClick={handleRefresh}
          className="poem-detail-action text-xs sm:text-sm hover:underline flex items-center gap-1"
          title="Refresh comments"
          disabled={isRefreshing}
        >
          <FaSync className={`w-3 h-3 ${isRefreshing ? "animate-spin" : ""}`} />
          <span className="hidden sm:inline">
            {isRefreshing ? "Refreshing..." : "Refresh"}
          </span>
        </button>
        {user && user._id === poem.author?._id && (
          <>
            <button
              onClick={() => navigate(`/edit-poem/${poem._id}`)}
              className="poem-detail-action text-xs sm:text-sm hover:underline"
            >
              Edit
            </button>
            <button
              onClick={() => {
                setModal({
                  open: true,
                  title: "Delete Poem?",
                  message:
                    "Are you sure you want to delete this poem? This action cannot be undone.",
                  type: "error",
                  onConfirm: handleDeletePoem,
                  confirmText: "Delete",
                });
              }}
              className="poem-detail-action text-xs sm:text-sm hover:underline"
            >
              Delete
            </button>
          </>
        )}
        {/* Pin/Unpin is available for any poem when logged in */}
        {user && (
          <button
            onClick={isPinnedByMe ? handleUnpinPoem : handlePinPoem}
            title={isPinnedByMe ? "Unpin from profile" : "Pin to profile"}
            className={`inline-flex items-center gap-1 text-xs sm:text-sm poem-detail-action hover:underline ${
              isPinnedByMe ? "poem-detail-action--active" : ""
            }`}
          >
            <FaThumbtack className="w-3 h-3" />
            <span className="hidden sm:inline">
              {isPinnedByMe ? "Pinned" : "Pin"}
            </span>
          </button>
        )}
        {user && user._id !== poem.author?._id && (
          <button
            onClick={() => setShowReportModal(true)}
            className="peer-home-link text-xs sm:text-sm hover:underline flex items-center gap-1"
            title="Report this content"
          >
            <FaFlag className="w-3 h-3" />
            <span className="hidden sm:inline">Report</span>
          </button>
        )}
        </div>
      </header>
      {/* Title + body — same poem-compose typography as write/edit */}
      {(() => {
        function deriveAlignment(p) {
          if (p?.alignment) return p.alignment;
          try {
            const raw =
              typeof p?.content === "string"
                ? JSON.parse(p.content)
                : p?.content;
            const firstWithAlign = Array.isArray(raw?.blocks)
              ? raw.blocks.find((b) => b?.data && b.data.textAlign)
              : null;
            const a = firstWithAlign?.data?.textAlign;
            return ["left", "center", "right", "justify"].includes(a)
              ? a
              : "left";
          } catch {
            return "left";
          }
        }
        function deriveFont(p) {
          if (p?.font) return p.font;
          return "serif";
        }
        const align = deriveAlignment(poem);
        const fontClass =
          deriveFont(poem) === "sans"
            ? "font-sans"
            : deriveFont(poem) === "mono"
            ? "font-mono"
            : "font-serif";
        return (
          <div
            className={`poem-compose min-w-0 max-w-full overflow-x-clip poem-align-${align} ${fontClass}`}
          >
            <h1 className="poem-detail-title mb-2 break-words min-w-0 max-w-full">
              {poem.title}
            </h1>
            {poem.coverImage && (
              <figure className="poem-detail-cover">
                <img
                  src={getPoemImageUrl(poem.coverImage)}
                  alt={`Visual for ${poem.title || "poem"}`}
                  className="poem-detail-cover__img"
                />
              </figure>
            )}
            <div className="poem-detail-body mb-6 sm:mb-8 min-w-0 max-w-full">
              <div
                dangerouslySetInnerHTML={{ __html: poemHtml }}
                className="poem-content-inner"
              />
            </div>
          </div>
        );
      })()}

      {/* Tags */}
      {poem.tags && poem.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6">
          {poem.tags.map((tag, index) => (
            <span
              key={index}
              className="peer-home-tag text-sm cursor-pointer"
              onClick={() => navigate(`/?q=${encodeURIComponent(tag)}`)}
            >
              #{tag}
            </span>
          ))}
        </div>
      )}
      {/* Like, comment, share, save row */}
      <div className="flex flex-wrap items-center gap-3 sm:gap-8 poem-detail-meta poem-detail-meta-row text-base sm:text-lg mb-6 sm:mb-8 min-w-0 max-w-full">
        <button
          onClick={handleLike}
          className="flex items-center gap-1 focus:outline-none"
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
          }}
        >
          {(() => {
            const liked =
              user &&
              Array.isArray(poem.likes) &&
              poem.likes.some(
                (like) => String(like._id || like) === String(user._id)
              );
            const showLiked = Boolean(liked || likeAnimating);
            return (
              <>
                <LikeHeart
                  liked={showLiked}
                  animating={likeAnimating}
                  size={18}
                  likedColor={ACCENT}
                />
                <span
                  className={`ml-1 poem-detail-like-count ${
                    showLiked ? "poem-detail-like-count--active" : ""
                  }`}
                >
                  {poem.likes?.length || 0}
                </span>
              </>
            );
          })()}
        </button>
        <div className="flex items-center gap-1">
          <FaRegComment />
          <span>{getCommentCount(poem)}</span>
        </div>
        <div className="flex items-center gap-1" title="Views">
          <FaEye color="#6b7280" />
          <span>{poem.viewCount ?? 0}</span>
        </div>
        <button
          onClick={handleShare}
          className="flex items-center gap-1 focus:outline-none relative"
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
          }}
          title="Copy link"
          aria-label="Copy poem link"
        >
          <FaRegPaperPlane />
          {copied && (
            <span className="absolute left-1/2 -top-7 -translate-x-1/2 bg-[#333333] text-[#ffffff] text-xs rounded px-2 py-1 shadow">
              Copied!
            </span>
          )}
        </button>
        {canShareToGroup && (
          <button
            onClick={() => setShowShareToGroup(true)}
            className="flex items-center gap-1 focus:outline-none poem-detail-action"
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
            }}
            title="Share to group"
            aria-label="Share to group"
          >
            <FaUsers />
          </button>
        )}
        <button
          onClick={handleToggleSave}
          className="flex items-center gap-1 focus:outline-none"
          style={{
            background: "transparent",
            border: "none",
            cursor: saving ? "wait" : "pointer",
          }}
          aria-label={isSaved ? "Unsave poem" : "Save poem"}
          title={isSaved ? "Unsave" : "Save"}
        >
          {isSaved ? (
            <FaBookmark color={ACCENT} />
          ) : (
            <FaRegBookmark color="#6b7280" />
          )}
        </button>
      </div>
      {/* Author and date at bottom (with collaborators inline) */}
      <div className="flex items-center gap-2 poem-detail-meta text-sm sm:text-base mb-4 sm:mb-6">
        <span className="inline-flex items-center gap-2 flex-wrap">
          {(() => {
            const authors = [];
            const seen = new Set();
            if (poem?.author?.username) {
              authors.push(poem.author);
              if (poem.author?._id) seen.add(String(poem.author._id));
              else if (poem.author) seen.add(String(poem.author));
            }
            if (Array.isArray(collabLists.collaborators)) {
              for (const u of collabLists.collaborators) {
                if (!u?.username) continue;
                const uid = String(u._id || "");
                if (uid && seen.has(uid)) continue; // avoid duplicates
                // avoid re-adding primary author by username fallback
                if (
                  poem?.author?.username &&
                  u.username === poem.author.username
                )
                  continue;
                authors.push(u);
                if (uid) seen.add(uid);
              }
            }
            if (authors.length === 0) return "Unknown";

            // Avatar group (overlapping)
            const avatarGroup = (
              <span
                key="avatars"
                className="inline-flex items-center -space-x-2 mr-1"
              >
                {authors.slice(0, 3).map((u, i) => (
                  <img
                    key={`av-${u._id || i}`}
                    src={getAvatar(u)}
                    alt="avatar"
                    className={`rounded-full object-cover border-2 border-white ${
                      i === 0
                        ? "w-6 h-6 sm:w-7 sm:h-7"
                        : "w-5 h-5 sm:w-6 sm:h-6"
                    }`}
                    style={{ zIndex: 10 - i }}
                  />
                ))}
                {authors.length > 3 && (
                  <span
                    className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[#fff5ef] text-[#777777] text-[10px] sm:text-xs font-semibold flex items-center justify-center border-2 border-white"
                    style={{ zIndex: 10 - authors.length }}
                  >
                    +{authors.length - 3}
                  </span>
                )}
              </span>
            );

            const renderUser = (u, idx) => (
              <Link
                key={u._id || idx}
                to={u._id === user?._id ? "/profile" : `/users/${u.username}`}
                className="poem-detail-author-link hover:underline inline-flex items-center gap-1"
              >
                {u.username}
                {u.badge && <BadgeIcon type={u.badge} />}
              </Link>
            );
            const pieces = [];
            // Prepend avatar group before text names
            pieces.push(avatarGroup);
            authors.forEach((u, i) => {
              if (i > 0) {
                pieces.push(
                  <span key={`sep-${i}`} className="text-[#777777]">
                    {i === authors.length - 1 ? " and " : ", "}
                  </span>
                );
              }
              pieces.push(renderUser(u, i));
            });
            return pieces;
          })()}
        </span>
        <span className="mx-1">·</span>
        <span>
          Published on{" "}
          {new Date(poem.createdAt).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          })}
          {poem.updatedAt && 
            new Date(poem.updatedAt).getTime() - new Date(poem.createdAt).getTime() > 60000 && (
            <>
              <span className="mx-1">·</span>
              <span className="text-[#777777]">
                Updated on{" "}
                {new Date(poem.updatedAt).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </span>
            </>
          )}
        </span>
      </div>
      {poem?.copyrightEnabled ? (
        <div className="text-xs text-[#777777] mb-4">
          ©{" "}
          {poem.copyrightYear || new Date(poem.createdAt).getFullYear()}{" "}
          {(poem.copyrightHolder || poem.author?.username || "PeerPoetry").trim()}
        </div>
      ) : null}
      {/* Comments section */}
      <div className="poem-detail-comments-section mt-6 sm:mt-8 relative">
        <h2 className="poem-detail-section-title font-bold">
          Comments
        </h2>
        {/* Add comment input */}
        <div className="poem-detail-comment-box flex items-center gap-2.5 relative min-w-0">
          <img
            src={getAvatar(user)}
            alt="avatar"
            className="w-8 h-8 rounded-full object-cover flex-shrink-0"
          />
          <form
            onSubmit={handleAddComment}
            className="flex-1 flex gap-2 items-center min-w-0"
          >
            <div className="flex-1 min-w-0">
              <input
                type="text"
                ref={commentInputRef}
                value={commentText}
                onChange={(e) => {
                  setCommentText(e.target.value);
                  handleMentionChange(e, "comment");
                }}
                onKeyDown={(e) => handleMentionKeyDown(e, "comment")}
                placeholder="Add a comment..."
                className="poem-detail-input p-2 rounded w-full min-w-0 max-w-full box-border"
                required
              />
            </div>
            <button
              type="submit"
              className="poem-detail-btn shrink-0 px-4 py-1.5 text-sm"
            >
              Post
            </button>
          </form>
          {mention.open && mention.target === "comment" && (
            <div className="mention-suggest mt-1 absolute left-16 bottom-12">
              <ul>
                {(mention.items || []).map((u, i) => (
                  <li
                    key={u._id || i}
                    className={i === mention.index ? "active" : ""}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      applyMention(u.username);
                    }}
                  >
                    <img
                      src={getAvatarUrl(u.avatar)}
                      alt="avatar"
                      className="w-6 h-6 rounded-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src =
                          e.currentTarget.src === DEFAULT_AVATAR_DATA_URL
                            ? FALLBACK_AVATAR_SVG
                            : DEFAULT_AVATAR_DATA_URL;
                      }}
                    />
                    <span className="font-medium">{u.username}</span>
                    {u.name && (
                      <span className="text-[#777777] ml-1">({u.name})</span>
                    )}
                  </li>
                ))}
                {(!mention.items || mention.items.length === 0) && (
                  <li className="text-sm text-[#777777] px-3 py-2">No matches</li>
                )}
              </ul>
            </div>
          )}
        </div>
        {/* Comments list */}
        {!poem.comments || poem.comments.length === 0 ? (
          <EmptyState
            icon="comments"
            title="No comments yet"
            message={
              user
                ? "Be the first to share your thoughts on this poem. Your comment can inspire meaningful conversations."
                : "No one has commented on this poem yet. Sign in to be the first to share your thoughts!"
            }
            action={user ? undefined : "login"}
            actionLabel={user ? undefined : "Sign In to Comment"}
            actionLink={user ? undefined : "/login"}
            className="py-8"
          />
        ) : (
          <>
          {(poem.comments || []).map((comment, idx) => {
            const replies = comment.replies || [];
            const replyThreadKey = comment._id || String(idx);
            const repliesExpanded = expandedReplyThreads[replyThreadKey];
            const hasHiddenReplies =
              replies.length > REPLY_PREVIEW_COUNT && !repliesExpanded;
            const visibleReplies = hasHiddenReplies
              ? replies.slice(0, REPLY_PREVIEW_COUNT)
              : replies;

            return (
            <div
              key={replyThreadKey}
              className="border-t poem-detail-comment-border pt-3 sm:pt-4 pb-3"
            >
            <div className="flex gap-2 sm:gap-3">
              {/* Avatar */}
              <img
                src={getAvatar(comment.user)}
                alt="avatar"
                className="w-8 h-8 rounded-full object-cover mt-0.5 flex-shrink-0"
              />
              <div className="flex-1 relative pr-12 sm:pr-16 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-1">
                  {comment.user?._id ? (
                    <Link
                      to={
                        comment.user._id === user?._id
                          ? "/profile"
                          : `/users/${comment.user.username}`
                      }
                      className="poem-detail-comment-author hover:underline"
                    >
                      <span className="inline-flex items-center gap-1">
                        {comment.user?.username || "Anonymous"}
                        <BadgeIcon type={comment.user?.badge} />
                      </span>
                    </Link>
                  ) : (
                    <span className="poem-detail-comment-author">
                      {comment.user?.username || "Anonymous"}
                    </span>
                  )}
                  <span className="poem-detail-comment-time text-xs">
                    {timeAgo(comment.createdAt || comment.updatedAt)}
                  </span>
                </div>
                {editCommentId === comment._id ? (
                  <form
                    onSubmit={(e) => handleEditSubmit(e, comment._id)}
                    className="flex items-center gap-2 mt-1"
                  >
                    <input
                      type="text"
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      className="poem-detail-input flex-1 p-1 text-sm rounded"
                    />
                    <button
                      type="submit"
                      className="poem-detail-btn-secondary text-xs px-2 py-1"
                    >
                      Save
                    </button>
                  </form>
                ) : (
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="poem-detail-comment-text min-w-0 flex-1 break-words">
                      {renderWithMentions(comment.text)}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLikeComment(comment._id);
                      }}
                      className="flex items-center gap-1 focus:outline-none whitespace-nowrap flex-shrink-0"
                      style={{ background: "transparent", border: "none", cursor: "pointer" }}
                    >
                      {(() => {
                        const liked =
                          user &&
                          Array.isArray(comment.likes) &&
                          comment.likes.some(
                            (id) => String(id._id || id) === String(user._id)
                          );
                        const animating = Boolean(
                          commentLikeAnimating[comment._id]
                        );
                        return (
                          <>
                            <LikeHeart
                              liked={Boolean(liked || animating)}
                              animating={animating}
                              size={14}
                              likedColor={ACCENT}
                            />
                            <span className="ml-1 poem-detail-like-count">
                              {comment.likes?.length || 0}
                            </span>
                          </>
                        );
                      })()}
                    </button>
                  </div>
                )}
                {/* Action buttons: Reply, Edit, Delete side by side */}
                <div className="flex flex-wrap gap-2 sm:gap-3 mt-2 ml-0 sm:ml-8">
                  <button
                    type="button"
                    onClick={() =>
                      startReply(comment._id, { threadKey: replyThreadKey })
                    }
                    className="poem-detail-action text-xs hover:underline"
                  >
                    Reply
                  </button>
                  {user &&
                    (comment.user?._id === user._id ||
                      user.role === "admin") && (
                      <>
                        <button
                          onClick={() => {
                            setEditCommentId(comment._id);
                            setEditText(comment.text);
                          }}
                          className="poem-detail-action text-xs hover:underline"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => {
                            setModal({
                              open: true,
                              title: "Delete Comment?",
                              message:
                                "Are you sure you want to delete this comment? This action cannot be undone.",
                              type: "error",
                              onConfirm: () => handleDeleteComment(comment._id),
                              confirmText: "Delete",
                            });
                          }}
                          className="peer-home-link text-xs hover:underline"
                        >
                          Delete
                        </button>
                      </>
                    )}
                </div>
              </div>
            </div>
            {(visibleReplies.length > 0 ||
              replyingToCommentId === comment._id ||
              hasHiddenReplies) && (
              <div className="poem-detail-replies">
                {visibleReplies.map((reply, i) => (
                  <div
                    key={reply._id || i}
                    className="poem-detail-reply"
                  >
                    <img
                      src={getAvatar(reply.user)}
                      alt="avatar"
                      className="w-8 h-8 rounded-full object-cover mt-1 flex-shrink-0"
                    />
                    <div className="poem-detail-reply__body">
                      <div className="flex items-center gap-2">
                        {reply.user?._id ? (
                          <Link
                            to={
                              reply.user._id === user?._id
                                ? "/profile"
                                : `/users/${reply.user.username}`
                            }
                            className="poem-detail-comment-author hover:underline"
                          >
                            <span className="inline-flex items-center gap-1">
                              {reply.user?.username || "Anonymous"}
                              <BadgeIcon type={reply.user?.badge} />
                            </span>
                          </Link>
                        ) : (
                          <span className="poem-detail-comment-author">
                            {reply.user?.username || "Anonymous"}
                          </span>
                        )}
                        <span className="poem-detail-comment-time text-xs">
                          {timeAgo(reply.createdAt || reply.updatedAt)}
                        </span>
                      </div>
                      {editReply.commentId === comment._id &&
                      editReply.replyId === reply._id ? (
                        <form
                          onSubmit={(e) =>
                            handleEditReplySubmit(e, comment._id, reply._id)
                          }
                          className="flex items-center gap-2 mt-1"
                        >
                          <input
                            type="text"
                            value={editReplyText}
                            onChange={(e) => setEditReplyText(e.target.value)}
                            className="poem-detail-input flex-1 p-1 text-sm rounded"
                          />
                          <button
                            type="submit"
                            className="poem-detail-btn-secondary text-xs px-2 py-1"
                          >
                            Save
                          </button>
                        </form>
                      ) : (
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div className="poem-detail-comment-text break-words min-w-0 flex-1">
                            {renderWithMentions(reply.text)}
                          </div>
                          {user && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleLikeReply(comment._id, reply._id);
                              }}
                              className="flex items-center gap-1 focus:outline-none whitespace-nowrap flex-shrink-0"
                              style={{ background: "transparent", border: "none", cursor: "pointer" }}
                            >
                              {(() => {
                                const liked =
                                  user &&
                                  Array.isArray(reply.likes) &&
                                  reply.likes.some(
                                    (id) =>
                                      String(id._id || id) === String(user._id)
                                  );
                                const replyKey = `${comment._id}-${reply._id}`;
                                const animating = Boolean(
                                  replyLikeAnimating[replyKey]
                                );
                                return (
                                  <>
                                    <LikeHeart
                                      liked={Boolean(liked || animating)}
                                      animating={animating}
                                      size={13}
                                      likedColor={ACCENT}
                                    />
                                    <span className="ml-1 poem-detail-like-count">
                                      {reply.likes?.length || 0}
                                    </span>
                                  </>
                                );
                              })()}
                            </button>
                          )}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-2 sm:gap-3 mt-1">
                        <button
                          type="button"
                          onClick={() =>
                            startReply(comment._id, {
                              replyId: reply._id,
                              username: reply.user?.username,
                              threadKey: replyThreadKey,
                              allReplies: replies,
                            })
                          }
                          className="poem-detail-action text-xs hover:underline"
                        >
                          Reply
                        </button>
                        {user &&
                          (reply.user?._id === user._id ||
                            user.role === "admin") && (
                            <>
                              <button
                                type="button"
                                onClick={() => {
                                  setEditReply({
                                    commentId: comment._id,
                                    replyId: reply._id,
                                  });
                                  setEditReplyText(reply.text);
                                }}
                                className="poem-detail-action text-xs hover:underline"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setModal({
                                    open: true,
                                    title: "Delete Reply?",
                                    message:
                                      "Are you sure you want to delete this reply? This action cannot be undone.",
                                    type: "error",
                                    onConfirm: () =>
                                      handleDeleteReply(
                                        comment._id,
                                        reply._id
                                      ),
                                    confirmText: "Delete",
                                  });
                                }}
                                className="peer-home-link text-xs hover:underline"
                              >
                                Delete
                              </button>
                            </>
                          )}
                      </div>
                    </div>
                  </div>
                ))}
                {hasHiddenReplies && (
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedReplyThreads((prev) => ({
                        ...prev,
                        [replyThreadKey]: true,
                      }))
                    }
                    className="poem-detail-action text-xs hover:underline mt-3"
                  >
                    Show {replies.length - REPLY_PREVIEW_COUNT} more{" "}
                    {replies.length - REPLY_PREVIEW_COUNT === 1
                      ? "reply"
                      : "replies"}
                  </button>
                )}
                {replyingToCommentId === comment._id &&
                  renderReplyForm(comment._id)}
              </div>
            )}
            </div>
          );
          })}
          {commentsPagination.hasNextPage && (
              <button
                type="button"
                onClick={loadMoreComments}
                disabled={loadingMoreComments}
                className="poem-detail-action text-sm hover:underline mt-2 w-full text-left border-t poem-detail-comment-border pt-4 disabled:opacity-60"
              >
                {loadingMoreComments
                  ? "Loading comments..."
                  : `Load more comments${
                      typeof commentsPagination.totalItems === "number" &&
                      poem.comments?.length
                        ? ` (${Math.max(
                            0,
                            commentsPagination.totalItems -
                              poem.comments.length
                          )} remaining)`
                        : ""
                    }`}
              </button>
            )}
          </>
        )}
      </div>
      {showLoginModal && (
        <LoginModal onClose={() => setShowLoginModal(false)} />
      )}

      {/* Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 bg-[#333333]/50 overflow-y-auto h-full w-full z-50 p-4">
          <div className="relative top-10 sm:top-20 mx-auto p-5 w-full max-w-sm shadow-lg poem-detail-report-modal box-border">
            <div className="mt-3">
              <h3 className="poem-detail-section-title text-lg font-bold mb-4">
                Report Content
              </h3>
              <p className="text-sm text-[#777777] mb-4">
                Please provide a reason for reporting this content. This helps
                us understand the issue and take appropriate action.
              </p>
              <textarea
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                placeholder="Please describe why you're reporting this content..."
                className="poem-detail-input w-full p-3 rounded resize-none h-24 text-sm"
                maxLength={500}
              />
              <div className="flex justify-end space-x-3 mt-4">
                <button
                  onClick={() => {
                    setShowReportModal(false);
                    setReportReason("");
                  }}
                  className="px-4 py-2 poem-detail-btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleReport}
                  className="px-4 py-2 poem-detail-btn"
                >
                  Submit Report
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ShareToGroupModal
        open={showShareToGroup}
        poemId={poem?._id}
        poemTitle={poem?.title}
        onClose={() => setShowShareToGroup(false)}
        onShared={() => {
          toast.success("Poem shared to group!");
        }}
      />
        </article>
        <aside className="poem-detail-page__rail">
          <HomeRightSidebar />
        </aside>
      </div>
    </div>
  );
}

export default PoemDetail;
